import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  DataGridPro,
  type DataGridProProps,
  type GridCallbackDetails,
  type GridColumnVisibilityModel,
  type GridInitialState,
  type GridRowParams,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ActionDialog } from "~/components/action_dialog";
import { LinkableGridRow } from "~/components/linkable_grid_row";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { DataGridScrollShadow } from "~/components/scrollable_data_grid_container";

// NOTE `noRowsLabel` is converted to a mandatory prop so that it complies
// with Trustap's design system where a custom message has to be used instead of
// MUI's default: "No rows.".
type DataGridWithNoRowsLabelProps<R extends GridValidRowModel> = Omit<
  DataGridProProps<R>,
  "localeText" | "onRowClick"
> & {
  localeText: Omit<
    NonNullable<DataGridProProps<R>["localeText"]>,
    "noRowsLabel"
  > & {
    noRowsLabel: string;
  };
  // MUI types onRowClick's params as GridRowParams<any> (the event lookup is
  // not parameterized by the row), so re-declare it R-typed; it stays
  // assignable to MUI's looser handler when forwarded to DataGridPro.
  onRowClick?: (
    params: GridRowParams<R>,
    event: MouseEvent<HTMLElement>,
    details: GridCallbackDetails,
  ) => void;
};

type ExportPropSet =
  | {
      handleExport: (callback: () => void) => Promise<void>;
      isExporting?: boolean;
      exportDialogHeading?: string;
      exportDialogMessage?: string;
      handleCancelExport?: () => void;
    }
  | {
      handleExport?: undefined;
      isExporting?: undefined;
      exportDialogHeading?: undefined;
      exportDialogMessage?: undefined;
      handleCancelExport?: undefined;
    };

type ReadableDataGridProProps<R extends GridValidRowModel> =
  DataGridWithNoRowsLabelProps<R> &
    ExportPropSet & {
      storageKey?: string;
      dataTestId?: string;
      onRefresh?: () => void;
      showAllRows?: boolean;
      // Hides the row of controls above the grid (result count, refresh,
      // column picker, layout reset) for the grids whose design doesn't have
      // one. The `handleExport` button lives in that row too, so a grid can't
      // hide the toolbar and still offer an export from it.
      hideToolbar?: boolean;
      // When set, rows render via `LinkableGridRow`: left-click navigates with
      // `onNavigate`, middle/right-click open the URL in a new tab / show the
      // row menu. The two functions are grouped so a grid can't enable
      // navigation with only half of it wired up.
      rowNavigation?: {
        getUrl: (row: R) => string;
        onNavigate: (url: string) => void;
      };
    };

export function ReadableDataGrid<
  R extends GridValidRowModel = GridValidRowModel,
>({
  rowCount,
  columns,
  storageKey,
  onColumnVisibilityModelChange: userOnVisibilityChange,
  onColumnWidthChange: userOnWidthChange,
  handleExport,
  isExporting,
  exportDialogHeading,
  exportDialogMessage,
  handleCancelExport,
  dataTestId,
  onRefresh,
  rowNavigation,
  showAllRows,
  hideToolbar,
  ...restProps
}: ReadableDataGridProProps<R>) {
  const lsKey = useMemo(
    () =>
      storageKey ??
      `ReadableDataGrid:${typeof window !== "undefined" ? window.location.pathname : ""}`,
    [storageKey],
  );

  const storageItemKey = `${lsKey}:gridState`;

  const {
    initialState: userInitialState,
    slots: userSlots,
    slotProps: userSlotProps,
    ...gridRestProps
  } = restProps;

  // When a grid opts into row navigation, swap in the linkable row slot and
  // feed it the caller's URL builder + navigate callback. Otherwise leave the
  // caller's own slots untouched.
  const slots = rowNavigation
    ? { ...userSlots, row: LinkableGridRow }
    : userSlots;

  // Read once from localStorage at mount. Subsequent updates flow through
  // setSavedState rather than re-reading storage.
  const [savedState, setSavedState] = useState<
    Pick<GridInitialState, "columns"> | undefined
  >(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    try {
      const raw = localStorage.getItem(storageItemKey);
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as Pick<GridInitialState, "columns">;
      return parsed.columns ? parsed : undefined;
    } catch {
      return undefined;
    }
  });

  const [resetCounter, setResetCounter] = useState(0);

  const initialVisibility: GridColumnVisibilityModel =
    savedState?.columns?.columnVisibilityModel ??
    userInitialState?.columns?.columnVisibilityModel ??
    {};

  const apiRef = useGridApiRef();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialVisibility);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [draftVisibilityModel, setDraftVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialVisibility);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // `savedState` read through a ref so persist-triggered updates don't
  // re-fire the restore effect below; only the inputs that cause MUI X to
  // reset column state belong in that effect's deps.
  const savedStateRef = useRef(savedState);
  savedStateRef.current = savedState;

  // Re-apply the persisted column layout (order, widths, hidden columns)
  // every time MUI X internally resets the column state. That happens on
  // mount, on remount-after-reset, and — crucially — whenever the parent
  // swaps the `columns` reference (e.g. async data loads) or whenever the
  // controlled `columnVisibilityModel` reference changes. MUI X's own
  // useGridColumns effect runs first (children before parents), wiping
  // dimensions/order; we then put them back. `restoreState` is used rather
  // than the `initialState` prop because the latter is unreliable when
  // controlled props are also supplied.
  useEffect(() => {
    const saved = savedStateRef.current;
    if (!saved?.columns) {
      return;
    }
    const id = requestAnimationFrame(() => {
      apiRef.current?.restoreState({ columns: saved.columns });
    });
    return () => cancelAnimationFrame(id);
  }, [apiRef, resetCounter, columns, columnVisibilityModel]);

  const persistColumns = useCallback(
    (override?: { columnVisibilityModel?: GridColumnVisibilityModel }) => {
      if (!apiRef.current) {
        return;
      }
      try {
        const exportedColumns = apiRef.current.exportState().columns ?? {};
        const next: Pick<GridInitialState, "columns"> = {
          columns: {
            ...exportedColumns,
            ...(override?.columnVisibilityModel !== undefined && {
              columnVisibilityModel: override.columnVisibilityModel,
            }),
          },
        };
        localStorage.setItem(storageItemKey, JSON.stringify(next));
        setSavedState(next);
      } catch (err) {
        console.error("Couldn't persist grid state:", err);
      }
    },
    [apiRef, storageItemKey],
  );

  const handleVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel, details: GridCallbackDetails) => {
      setColumnVisibilityModel(model);
      userOnVisibilityChange?.(model, details);
      persistColumns({ columnVisibilityModel: model });
    },
    [persistColumns, userOnVisibilityChange],
  );

  const handleWidthChange = useCallback<
    NonNullable<DataGridProProps["onColumnWidthChange"]>
  >(
    (params, event, details) => {
      userOnWidthChange?.(params, event, details);
      persistColumns();
    },
    [persistColumns, userOnWidthChange],
  );

  const handleOrderChange = useCallback(() => {
    persistColumns();
  }, [persistColumns]);

  const handleReset = useCallback(() => {
    try {
      localStorage.removeItem(storageItemKey);
    } catch (err) {
      console.error("Couldn't clear grid state:", err);
    }
    const propsVisibility =
      userInitialState?.columns?.columnVisibilityModel ?? {};
    setColumnVisibilityModel(propsVisibility);
    setSavedState(undefined);
    setResetCounter((c) => c + 1);
  }, [storageItemKey, userInitialState]);

  const canReset = savedState !== undefined;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const exportDialogHeadingText = exportDialogHeading ?? "Export transactions";
  const exportDialogMessageText =
    exportDialogMessage ??
    `You're about to export ${rowCount} transactions. Depending on the amount, the export might take a few minutes to complete.`;

  return (
    <Stack>
      {!hideToolbar && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="body2" component="span">
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Search results:{" "}
              </Box>
              <Typography
                variant="captionWithBackground"
                sx={{
                  backgroundColor: "action.selected",
                  px: 1,
                }}
              >
                {rowCount}
              </Typography>
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={2.5}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={onRefresh}
                variant="withBackground"
                sx={{ color: "darkCharcoal.900" }}
              >
                <PublicSvgIcon name="refresh" inheritColor />
              </IconButton>
            </Tooltip>
            <Tooltip title="Show / hide columns">
              <IconButton
                onClick={() => {
                  setDraftVisibilityModel(columnVisibilityModel);
                  setIsSettingsDialogOpen(true);
                }}
                variant="withBackground"
                sx={{ color: "darkCharcoal.900" }}
              >
                <PublicSvgIcon name="view_column_2" inheritColor />
              </IconButton>
            </Tooltip>
            {!isMobile && (
              <Tooltip title="Reset table layout">
                <span>
                  <IconButton
                    onClick={handleReset}
                    variant="withBackground"
                    disabled={!canReset}
                    sx={{ color: "darkCharcoal.900" }}
                  >
                    <PublicSvgIcon name="reset_table" inheritColor />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {handleExport && (
              <Button
                variant="text"
                onClick={() => setIsExportDialogOpen(true)}
                loading={!!isExporting}
                disabled={!rowCount}
                sx={{ px: 1, py: 0.75, borderRadius: 1 }}
                startIcon={
                  <PublicSvgIcon
                    name="ios_share"
                    inheritColor
                    sx={{ "& svg": { width: 12, height: 16 } }}
                  />
                }
              >
                Export
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      <DataGridScrollShadow>
        <DataGridPro<R>
          key={resetCounter}
          {...gridRestProps}
          pagination={!showAllRows}
          autoHeight={showAllRows || restProps.autoHeight}
          hideFooter={showAllRows}
          columns={columns}
          rowCount={
            restProps.paginationMode === "server" ? rowCount : undefined
          }
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleVisibilityModelChange}
          onColumnWidthChange={handleWidthChange}
          onColumnOrderChange={handleOrderChange}
          disableColumnResize={isMobile}
          apiRef={apiRef}
          disableColumnMenu
          initialState={userInitialState}
          slots={slots}
          slotProps={{
            ...userSlotProps,
            ...(rowNavigation && {
              row: {
                getUrl: rowNavigation.getUrl,
                onNavigate: rowNavigation.onNavigate,
              },
            }),
            ...(dataTestId && {
              root: { "data-testid": dataTestId },
            }),
            loadingOverlay: {
              noRowsVariant: "circular-progress",
            },
          }}
        />
      </DataGridScrollShadow>

      {isSettingsDialogOpen && (
        <ActionDialog
          heading="Choose columns"
          confirmButtonMessage="Save"
          cancelButtonMessage="Cancel"
          isDisabled={
            JSON.stringify(draftVisibilityModel) ===
            JSON.stringify(columnVisibilityModel)
          }
          confirm={() => {
            handleVisibilityModelChange(draftVisibilityModel, undefined!);
            setIsSettingsDialogOpen(false);
          }}
          cancel={() => setIsSettingsDialogOpen(false)}
        >
          <Stack spacing={1}>
            {columns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={draftVisibilityModel[col.field] !== false}
                    onChange={(_e: unknown, checked: boolean) =>
                      setDraftVisibilityModel((prev) => ({
                        ...prev,
                        [col.field]: checked,
                      }))
                    }
                  />
                }
                label={col.headerName ?? col.field}
              />
            ))}
          </Stack>
        </ActionDialog>
      )}

      {handleExport && isExportDialogOpen && (
        <ActionDialog
          heading={exportDialogHeadingText}
          message={exportDialogMessageText}
          confirmButtonMessage="Confirm export"
          cancelButtonMessage="No, cancel"
          isConfirming={isExporting}
          confirm={() => {
            handleExport(() => {
              setIsExportDialogOpen(false);
            });
          }}
          cancel={() => {
            if (isExporting && handleCancelExport) {
              handleCancelExport();
              setIsExportDialogOpen(false);
            } else {
              setIsExportDialogOpen(false);
            }
          }}
        />
      )}
    </Stack>
  );
}
